"use client";

// src/text.tsx
import * as React from "react";
import {
  Base,
  mergeStyles,
  systemForwardRef,
  useStyleConfig
} from "@stepstone-genesis/system";

// src/styles.ts
var root = {
  fontFamily: "fontFamilyBody",
  fontWeight: "fontWeightNormal",
  color: "colorTextPrimaryDefault"
};
var truncate = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
  wordBreak: "break-word"
};
var variants = {
  xxs: {
    fontSize: "fontSizeXxs",
    lineHeight: "lineHeightXxs"
  },
  xs: {
    fontSize: "fontSizeXs",
    lineHeight: "lineHeightXs"
  },
  small: {
    fontSize: "fontSizeS",
    lineHeight: "lineHeightS"
  },
  medium: {
    fontSize: "fontSizeM",
    lineHeight: "lineHeightM"
  },
  large: {
    fontSize: "fontSizeL",
    lineHeight: "lineHeightL"
  },
  xl: {
    fontSize: "fontSizeXl",
    lineHeight: "lineHeightXl"
  },
  xxl: {
    fontSize: "fontSizeXxl",
    lineHeight: "lineHeightXxl"
  },
  xxxl: {
    fontSize: "fontSizeXxxl",
    lineHeight: "lineHeightXxxl"
  },
  "4xl": {
    fontSize: "fontSize4Xl",
    lineHeight: "lineHeight4Xl"
  },
  jumbo: { fontSize: "fontSizeXxxl", lineHeight: "lineHeightXxxl" },
  jumboPlus: { fontSize: "fontSize4Xl", lineHeight: "lineHeight4Xl" },
  heading4: {
    fontSize: "fontSizeM",
    lineHeight: "lineHeightM"
  },
  heading3: {
    fontSize: "fontSizeL",
    lineHeight: "lineHeightL"
  },
  heading2: {
    fontSize: "fontSizeXl",
    lineHeight: "lineHeightXl"
  },
  heading1: {
    fontSize: "fontSizeXxl",
    lineHeight: "lineHeightXxl"
  },
  body: {
    fontSize: "fontSizeM",
    lineHeight: "lineHeightM",
    fontWeight: "fontWeightNormal"
  },
  bodyBold: {
    fontSize: "fontSizeM",
    lineHeight: "lineHeightM",
    fontWeight: "fontWeightBold"
  },
  bodyBoldUnderlined: {
    fontSize: "fontSizeM",
    lineHeight: "lineHeightM",
    fontWeight: "fontWeightBold",
    textDecoration: "underline"
  },
  bodyUnderlined: {
    fontSize: "fontSizeM",
    lineHeight: "lineHeightM",
    fontWeight: "fontWeightMedium",
    textDecoration: "underline"
  },
  bodyMedium: {
    fontSize: "fontSizeM",
    lineHeight: "lineHeightM",
    fontWeight: "fontWeightMedium"
  },
  bodyLarge: {
    fontSize: "fontSizeL",
    lineHeight: "componentParagraphBodyLargeLineHeight",
    fontWeight: "fontWeightNormal"
  },
  caption: {
    fontSize: "fontSizeXs",
    lineHeight: [
      "lineHeightXxs",
      "lineHeightXxs",
      "componentParagraphCaptionLineHeight"
    ],
    fontWeight: "fontWeightNormal"
  },
  captionMedium: {
    fontSize: "fontSizeXs",
    lineHeight: "componentParagraphCaptionLineHeight",
    fontWeight: "fontWeightMedium"
  },
  footnote: {
    fontSize: "fontSizeS",
    lineHeight: "lineHeightXs",
    fontWeight: "fontWeightNormal"
  },
  footnoteBold: {
    fontSize: "fontSizeS",
    lineHeight: "lineHeightXs",
    fontWeight: "fontWeightBold"
  },
  footnoteMedium: {
    fontSize: "fontSizeS",
    lineHeight: "lineHeightXs",
    fontWeight: "fontWeightMedium"
  },
  mini: {
    fontSize: "fontSizeXxs",
    lineHeight: "componentParagraphMiniLineHeight",
    fontWeight: "fontWeightMedium"
  },
  title: {
    fontSize: [
      "componentHeadingTitleMobileFontSize",
      "componentHeadingTitleMobileFontSize",
      "componentHeadingTitleFontSize"
    ],
    lineHeight: [
      "lineHeightXl",
      "lineHeightXl",
      "componentHeadingTitleLineHeight"
    ],
    fontWeight: "fontWeightMedium"
  },
  titleLarge: {
    fontSize: [
      "componentHeadingTitleLargeMobileFontSize",
      "componentHeadingTitleLargeMobileFontSize",
      "componentHeadingTitleLargeFontSize"
    ],
    lineHeight: [
      "componentHeadingTitleLargeMobileLineHeight",
      "componentHeadingTitleLargeMobileLineHeight",
      "componentHeadingTitleLargeLineHeight"
    ],
    fontWeight: "fontWeightBold"
  },
  heading: {
    fontSize: [
      "componentHeadingHeadingMobileFontSize",
      "componentHeadingHeadingMobileFontSize",
      "fontSizeXl"
    ],
    lineHeight: [
      "lineHeightL",
      "lineHeightL",
      "componentHeadingHeadingLineHeight"
    ],
    fontWeight: "fontWeightBold"
  },
  subheading: {
    fontSize: ["fontSizeL", "fontSizeL", "componentHeadingSubHeadingFontSize"],
    lineHeight: [
      "componentHeadingSubHeadingMobileLineHeight",
      "componentHeadingSubHeadingMobileLineHeight",
      "lineHeightL"
    ],
    fontWeight: "fontWeightMedium"
  },
  megaHero: {
    fontSize: [
      "componentHeadingHeroFontSize",
      "componentHeadingHeroFontSize",
      "componentHeadingMegaHeroFontSize"
    ],
    lineHeight: [
      "componentHeadingHeroLineHeight",
      "componentHeadingHeroLineHeight",
      "componentHeadingMegaHeroLineHeight"
    ],
    fontWeight: "fontWeightBold"
  },
  superHero: {
    fontSize: [
      "fontSizeXxxl",
      "fontSizeXxxl",
      "componentHeadingSuperHeroFontSize"
    ],
    lineHeight: [
      "lineHeightXxxl",
      "lineHeightXxxl",
      "componentHeadingSuperHeroLineHeight"
    ],
    fontWeight: "fontWeightBold"
  },
  hero: {
    fontSize: [
      "componentHeadingHeroMobileFontSize",
      "componentHeadingHeroMobileFontSize",
      "componentHeadingHeroFontSize"
    ],
    lineHeight: [
      "lineHeightXxl",
      "lineHeightXxl",
      "componentHeadingHeroLineHeight"
    ],
    fontWeight: "fontWeightBold"
  }
};
var styles = {
  root,
  variants,
  truncate
};

// src/text.tsx
var Text = systemForwardRef(function Text2({
  children,
  fontFamily,
  fontSize,
  fontWeight,
  lineHeight,
  textAlign,
  textDecoration,
  textTransform,
  wordBreak,
  wordWrap,
  variant,
  size = "medium",
  truncate: truncate2 = false,
  // @ts-expect-error - we need root but don't want it exposed
  __root,
  ...rest
}, ref) {
  const { TEXT } = useStyleConfig("TEXT", {
    // This merge order was how it used to work - don't ask me why :-)
    mergeOrder: ["base", "responsiveVariants", "variants"],
    styles: {
      base: mergeStyles([
        styles.root,
        truncate2 ? styles.truncate : {},
        __root ? __root : {},
        fontFamily ? { fontFamily } : {},
        fontSize ? { fontSize } : {},
        fontWeight ? { fontWeight } : {},
        lineHeight ? { lineHeight } : {},
        textAlign ? { textAlign } : {},
        textDecoration ? { textDecoration } : {},
        textTransform ? { textTransform } : {},
        wordBreak ? { wordBreak } : {},
        wordWrap ? { wordWrap } : {}
      ]),
      responsiveVariants: [
        {
          id: "variants",
          prop: variant || size,
          variants: styles.variants
        }
      ]
    },
    props: {
      fontFamily,
      fontSize,
      fontWeight,
      lineHeight,
      textAlign,
      textDecoration,
      textTransform,
      wordBreak,
      wordWrap,
      size,
      truncate: truncate2
    }
  });
  return /* @__PURE__ */ React.createElement(Base, { as: "span", ref, __root: TEXT.styles, element: "TEXT", ...rest }, children);
});
export {
  Text,
  styles
};
